import { useLayoutEffect, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";

const ACTIONS_SLOT_ID = "header-actions-slot";
const MOBILE_ACTIONS_SLOT_ID = "mobile-header-actions-slot";
const TITLE_SLOT_ID = "header-title-slot";

/**
 * Portal target for action buttons — visible on md+ screens inside the header.
 */
export function HeaderActionsSlot() {
  return <div id={ACTIONS_SLOT_ID} className="hidden md:flex items-center gap-1 lg:gap-2" />;
}

/**
 * Portal target for action buttons — visible on mobile, rendered just below the header.
 * Uses empty:hidden so it takes no space when no page has active header actions.
 */
export function MobileHeaderActionsSlot() {
  return (
    <div
      id={MOBILE_ACTIONS_SLOT_ID}
      className="flex md:hidden items-center gap-2 flex-wrap px-4 py-2 border-b border-border/40 empty:hidden"
    />
  );
}

/**
 * Portal target for record name/dropdown (left side, after back link).
 */
export function HeaderTitleSlot() {
  return <span id={TITLE_SLOT_ID} className="inline-flex items-center" />;
}

/**
 * Renders children into both header slots — desktop header (md+) and mobile page-top slot.
 */
export function HeaderActions({ children }: { children: ReactNode }) {
  const [headerSlot, setHeaderSlot] = useState<HTMLElement | null>(null);
  const [mobileSlot, setMobileSlot] = useState<HTMLElement | null>(null);
  useLayoutEffect(() => {
    setHeaderSlot(document.getElementById(ACTIONS_SLOT_ID));
    setMobileSlot(document.getElementById(MOBILE_ACTIONS_SLOT_ID));
  }, []);
  return (
    <>
      {headerSlot ? createPortal(children, headerSlot) : null}
      {mobileSlot ? createPortal(children, mobileSlot) : null}
    </>
  );
}

/**
 * Renders children into the header title slot (left side, after breadcrumb).
 */
export function HeaderTitle({ children }: { children: ReactNode }) {
  const [slot, setSlot] = useState<HTMLElement | null>(null);
  useLayoutEffect(() => { setSlot(document.getElementById(TITLE_SLOT_ID)); }, []);
  if (!slot) return null;
  return createPortal(children, slot);
}

const SEARCH_LEFT_SLOT_ID = "header-search-left-slot";
const SEARCH_RIGHT_SLOT_ID = "header-search-right-slot";

/** Portal target for a control on the LEFT side inside the header search bar. */
export function HeaderSearchLeftSlot() {
  return <div id={SEARCH_LEFT_SLOT_ID} className="flex items-center overflow-hidden rounded-l-full empty:hidden" />;
}

/** Portal target for a control on the RIGHT side inside the header search bar. */
export function HeaderSearchRightSlot() {
  return <div id={SEARCH_RIGHT_SLOT_ID} className="flex items-center overflow-hidden rounded-r-full empty:hidden" />;
}

/** Renders children into the search-bar left slot. */
export function HeaderSearchLeft({ children }: { children: ReactNode }) {
  const [slot, setSlot] = useState<HTMLElement | null>(null);
  useLayoutEffect(() => { setSlot(document.getElementById(SEARCH_LEFT_SLOT_ID)); }, []);
  if (!slot) return null;
  return createPortal(children, slot);
}

/** Renders children into the search-bar right slot. */
export function HeaderSearchRight({ children }: { children: ReactNode }) {
  const [slot, setSlot] = useState<HTMLElement | null>(null);
  useLayoutEffect(() => { setSlot(document.getElementById(SEARCH_RIGHT_SLOT_ID)); }, []);
  if (!slot) return null;
  return createPortal(children, slot);
}
